import { useAuthQuery } from '@/services/auth';
import { useListWorkspaces } from '@/features/workspaces/hooks';
import {
  announceCallbackComplete,
  clearAttemptedTransitionUrl,
  getAttemptedTransitionUrl,
  setAttemptedTransitionUrl,
  subscribeToRefreshAuth,
  subscribeToRefreshWorkspaces,
} from '@customerio/hydra-nav-state';
import { PermissionsProvider } from '@customerio/hydra-permissions/react';
import { type RoleDefinition } from '@customerio/hydra-permissions/roles';
import { isStandardRole, useRoleDefinitions } from '@/features/roles/hook';
import { Fragment, useEffect, useMemo, useRef } from 'react';
import { createSearchParams, Outlet, useLocation } from 'react-router-dom';
import { MainNavigation } from './components/main-navigation';
import { isEmptyString } from '@/utils/string';

import { RecordPerformanceMetric } from '@/components/record-performance-metric';
import { RocketMissionControl } from '@/components/rocket-mission-control';
import { useActiveBundle } from '@/context/active-bundle';
import { CurrentWorkspaceProvider, useCurrentWorkspace } from '@/context/current-workspace';
import { PerformanceMetric, PerformanceProvider } from '@/context/performance';
import { SidebarRoot } from '@/features/sidebar';
import { GlobalCSSVariable, useThemeContext } from '@/context/theme';
import { appContainerFadedOutStore, appContainerOutletRenderedStore } from '@/stores/root';
import { AccountState } from '@/utils/account';
import { analytics } from '@/utils/analytics';
import { useIsSettings } from '@/utils/navigation';
import { AppBundle } from '@/utils/app-bundles';
import { useSampleWorkspace } from '@/utils/sample-workspace';
import tracking from '@/utils/tracking-scripts';
import type { PermissionsUser } from '@customerio/hydra-permissions/dist/validation';
import { UserMasqPermission } from '@customerio/hydra-permissions/masq';
import { PageLoader } from '@customerio/pluma-components/react';
import { useStore } from '@nanostores/react';
import { ErrorBoundary } from 'react-error-boundary';
import { SettingsNavigation } from './components/settings-navigation';
import { TopNavigation } from './components/top-navigation';
import { SampleWorkspaceBorder } from './components/top-navigation/components/sample-workspace';
import { WorkspaceStripe } from './components/workspace-stripe';
import {
  sentrySetDataCenter,
  sentrySetUser,
  setSentryFullStorySessionUrl,
  setSentryMasqueradingContext,
} from '@/utils/sentry';
import { Navigate } from '@/utils/navigate';
import { isDemoAccount } from '@/utils/demo-accounts';
import { getWorkspaceIdFromUrl } from '@/context/current-workspace';
import { AuthProvider } from '@/services/auth/context';
import { authStore } from '@/services/auth/store';
import type { AccountPermission } from '@customerio/hydra-permissions';
import { accountPermissionsForRole } from '@customerio/hydra-permissions/roles';
import { Region } from '@/services/auth/types';
import { ROCKET_DISMISSED_KEY } from '@/components/rocket';

let didIdentify = false;

function AuthenticatedContent() {
  const auth = useAuthQuery();
  const workspaces = useListWorkspaces();
  const currentWorkspace = useCurrentWorkspace();
  const appContainerFadedOut = useStore(appContainerFadedOutStore);
  const appContainerOutletRendered = useStore(appContainerOutletRenderedStore);
  const themeContext = useThemeContext();
  const location = useLocation();
  const { isSettingsPage, isAnySettingsPage } = useIsSettings();
  const { isSampleWorkspace } = useSampleWorkspace();

  const showMainNav = !isAnySettingsPage;
  const showSettingSideNav = isAnySettingsPage;

  const currentWorkspaceIdRef = useRef(currentWorkspace?.id != null ? `${currentWorkspace.id}` : null);
  const shouldScheduleAuthRefresh = useRef(false);
  const refreshAuthCallbackId = useRef<string | null>(null);

  useEffect(() => {
    // If CDP is enabled, set the top nav height to 40px to make sure child apps
    // have the correct CSS variable values
    themeContext.setVariable(GlobalCSSVariable.TopNavHeight, '40px');
  });

  useEffect(() => {
    return subscribeToRefreshAuth(async (event) => {
      const workspaceIdFromUrl = getWorkspaceIdFromUrl(window.location.href);
      const currentWorkspaceId = currentWorkspaceIdRef.current != null ? `${currentWorkspaceIdRef.current}` : null;

      // If the workspace id in the URL is different from the current workspace id
      // from the useCurrentWorkspace hook,
      // schedule an auth.refetch once the URL matches the current workspace id.
      // This is necessary because the useAuth hook reads the workspace ID from the URL,
      // but the URL may not update immediately, causing the auth data to be stale.
      if (currentWorkspaceId != null && currentWorkspaceId !== workspaceIdFromUrl) {
        shouldScheduleAuthRefresh.current = true;
        refreshAuthCallbackId.current = event.detail.id;
      } else {
        try {
          await auth.refetch();
        } finally {
          announceCallbackComplete(event.detail.id);
          refreshAuthCallbackId.current = null;
        }
      }
    });
  }, []);

  useEffect(() => {
    const currentWorkspaceId = currentWorkspace?.id != null ? `${currentWorkspace.id}` : null;
    currentWorkspaceIdRef.current = currentWorkspaceId;
  }, [currentWorkspace?.id]);

  useEffect(() => {
    const workspaceIdFromUrl = getWorkspaceIdFromUrl(window.location.href);

    // If there is an auth refresh scheduled and the workspace IDs finally match,
    // call .refetch on the auth hook query.
    if (shouldScheduleAuthRefresh.current && currentWorkspaceIdRef.current === workspaceIdFromUrl) {
      shouldScheduleAuthRefresh.current = false;
      const cb = async () => {
        try {
          await auth.refetch();
        } finally {
          if (refreshAuthCallbackId.current != null) {
            announceCallbackComplete(refreshAuthCallbackId.current);
            refreshAuthCallbackId.current = null;
          }
        }
      };
      cb();
    }
  }, [location.pathname, currentWorkspace?.id]);

  const isRefreshingAuthSampleWorkspace = useRef(false);
  useEffect(() => {
    // Switching to the sample workspace doesn't trigger
    // `subscribeToRefreshAuth`. If the current user doesn't have a role
    // for the sample workspace, refresh the auth data.
    if (currentWorkspace?.id != null && isSampleWorkspace(currentWorkspace.id)) {
      const workspaceId = currentWorkspace.id;
      const hasRoleForSampleWorkspace = auth.data?.user?.roles?.some((r) => r.workspace_id === workspaceId) ?? false;

      if (!hasRoleForSampleWorkspace && !auth.isLoading && !isRefreshingAuthSampleWorkspace.current) {
        const cb = async () => {
          isRefreshingAuthSampleWorkspace.current = true;
          try {
            await auth.refetch();
          } finally {
            isRefreshingAuthSampleWorkspace.current = false;
          }
        };
        cb();
      }
    }
  }, [isSampleWorkspace, currentWorkspace?.id, auth.data?.user?.roles, auth.isLoading]);

  useEffect(() => {
    return subscribeToRefreshWorkspaces(async (event) => {
      try {
        await workspaces.refetch();
      } finally {
        announceCallbackComplete(event.detail.id);
      }
    });
  }, []);

  useEffect(() => {
    const showTopColor = !isSettingsPage && currentWorkspace != null && !isEmptyString(currentWorkspace.color);

    if (showTopColor && currentWorkspace != null) {
      themeContext.setVariable(GlobalCSSVariable.WorkspaceBorderColor, currentWorkspace.color);
      themeContext.setVariable(GlobalCSSVariable.WorkspaceBorderHeight, '12px');
    } else {
      themeContext.setVariable(GlobalCSSVariable.WorkspaceBorderHeight, '0px');
    }
  }, [currentWorkspace, isSettingsPage]);

  // Additional Sentry setup once the user is authenticated
  useEffect(() => {
    if (auth.isSuccess === false || auth.data == null) {
      return;
    }

    const { account, user } = auth.data;

    if (account == null || user == null) {
      return;
    }

    sentrySetUser(account, user);
    sentrySetDataCenter(account.region === Region.EU ? 'eu' : 'us');
    setSentryFullStorySessionUrl();
    setSentryMasqueradingContext(account.is_masq);
  }, [auth.data, auth.isSuccess]);

  // inject tracking scripts once the user is authenticated
  useEffect(() => {
    if (auth.isSuccess === true && auth.data != null && !didIdentify) {
      didIdentify = true;
      const { user } = auth.data;

      // identify
      void analytics?.identify(`${user?.id}`, {
        id: `${user?.id}`,
        email: user?.email,
      });
    }
  }, [auth.data, auth.isSuccess]);

  useEffect(() => {
    void analytics?.page();
  }, [location]);

  // clean tracking scripts + analytics once the component is unmounted
  useEffect(() => {
    return () => {
      tracking.cleanup();
      void analytics?.reset();
    };
  }, []);

  // Using useMemo to make sure the attempted url setting runs before rendering,
  // i.e. before the Navigate component is rendered.
  useMemo(() => {
    if (auth.isLoading === true) {
      return;
    }

    if (auth.data == null) {
      const attemptedUrl = window.location.href.replace(/\/$/, '');
      if (getAttemptedTransitionUrl() == null && attemptedUrl !== window.location.origin) {
        setAttemptedTransitionUrl(attemptedUrl);
      }
    } else {
      clearAttemptedTransitionUrl();
    }
  }, [auth.data, auth.isLoading]);

  if (auth.isLoading === true) {
    return <PageLoader />;
  }

  if (auth.data == null || auth.data?.account == null || auth.data?.user == null) {
    return <Navigate to="/login" saveCurrentUrl={true} />;
  }

  if (workspaces.isLoading === true) {
    return <PageLoader />;
  }

  const { account, user } = auth.data;

  // If we're masqing but the current user doesn't have masq permissions set, or
  // the permissions are "none", redirect to settings page (which is accessible
  // to all users who can masq)
  if (account.is_masq === true && (user.noMasqAccess === true || user.masqLevelNotSet === true) && !isAnySettingsPage) {
    return (
      <Navigate
        to={{
          pathname: '/settings',
          search: createSearchParams({ masq_account_id: String(account.id) }).toString(),
        }}
      />
    );
  }

  const isTrial = account.account_state === AccountState.Free;
  let isRocketDismissed = false;
  try {
    isRocketDismissed = localStorage.getItem(ROCKET_DISMISSED_KEY) === 'true';
  } catch {
    // localStorage might fail in some edge cases. Prevent the error from crashing the app.
  }
  const shouldShowRocket =
    isTrial && !isSampleWorkspace(currentWorkspace?.id) && !isDemoAccount(account.id) && !isRocketDismissed;

  return (
    <AuthProvider auth={{ account, user }}>
      <RecordPerformanceMetric performanceMetric={PerformanceMetric.Authenticated} />
      <WorkspaceStripe />
      <TopNavigation />
      <div style={{ display: 'flex', minHeight: 0, flex: 1 }}>
        {showMainNav ? <MainNavigation /> : null}
        {showSettingSideNav ? <SettingsNavigation /> : null}
        <div
          id="embedded-app-container"
          style={{
            display: 'flex',
            flex: 1,
            minWidth: 0,
            overflow: 'auto',
            transition: 'opacity 0.25s ease',
            opacity: appContainerFadedOut ? 0 : 1,
          }}
        >
          {appContainerOutletRendered ? <Outlet /> : null}
        </div>
        <SidebarRoot />
        {shouldShowRocket ? (
          /* The Rocket component is experimental and isolated from the rest of the app,
            So any errors that could happen within the component can be contained */
          <ErrorBoundary fallback={<Fragment />}>
            <RocketMissionControl />
          </ErrorBoundary>
        ) : null}
      </div>
      <SampleWorkspaceBorder />
    </AuthProvider>
  );
}

function PermissionsProviderWrapper() {
  const auth = useStore(authStore);
  const roleDefinitions = useRoleDefinitions();

  const workspaceUserRoles =
    auth.user?.roles != null
      ? auth.user?.roles.reduce((acc, userRole) => {
          acc[userRole.workspace_id] = { id: userRole.role_id, name: userRole.role_name };
          return acc;
        }, {} as Record<number, { id?: number; name: string }>)
      : null;

  const user: PermissionsUser = {
    account_permissions: auth.user?.account_permissions,
    workspace_roles: workspaceUserRoles,
    masq_permissions: auth.user?.masq_permissions ?? UserMasqPermission.NoMasqAccess,
  };
  const workspace = useCurrentWorkspace();
  const activeApp = useActiveBundle();
  const workspaceId =
    (activeApp === AppBundle.Journeys ? workspace?.cio_workspace_id : workspace?.cdp_workspace_id) ?? null;

  const allRoles = useMemo(() => {
    if (roleDefinitions == null) {
      return null;
    }

    const rolesRecord: Record<string, RoleDefinition> = {};
    Object.values(roleDefinitions).forEach((role) => {
      let accountPermissions: Set<AccountPermission> = new Set();
      if (isStandardRole(role.name)) {
        accountPermissions = accountPermissionsForRole(role.name);
      } else {
        // custom roles don't have account permissions
        // so we populate what we have from the user
        accountPermissions = new Set(auth.user?.account_permissions ?? []);
      }

      // role names are unique
      rolesRecord[role.name] = {
        ...role,
        accountPermissions,
      };
    });

    return rolesRecord;
  }, [roleDefinitions, auth.user?.account_permissions]);

  return (
    <PermissionsProvider user={user} account={auth.account} roles={allRoles} workspaceId={workspaceId}>
      <PerformanceProvider>
        <AuthenticatedContent />
      </PerformanceProvider>
    </PermissionsProvider>
  );
}

export default function Authenticated() {
  return (
    <CurrentWorkspaceProvider>
      <PermissionsProviderWrapper />
    </CurrentWorkspaceProvider>
  );
}
